import type { ReactNode } from 'react';
import {
  CheckCircle2,
  MessageSquareWarning,
  RotateCcw,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { ApprovalState } from '@mg/shared';
import { Button } from '../../components/ui/Button';

export const SUBMITTABLE_STATES = new Set<ApprovalState>([
  ApprovalState.DRAFT,
  ApprovalState.REVISE,
]);
export const REVIEW_STATES = new Set<ApprovalState>([
  ApprovalState.IN_REVIEW,
  // Reviewer signed off, awaiting the final Approver (two-stage BOTH mode). Compliance is gated to
  // IN_REVIEW, so REVIEWED shows only Approve / Cancel / Request changes / Reject.
  ApprovalState.REVIEWED,
  ApprovalState.COMPLIANCE_CHECK,
]);

/** A bordered action row: a note on the left, buttons on the right. */
export function ActionRow({ note, children }: { note: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-card p-4 shadow-card">
      {/* A div, not a <p>: the note now carries a list of what is still unscheduled, and a <ul>
          inside a <p> is invalid markup that browsers silently reflow. */}
      <div className="text-sm text-ink-label">{note}</div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

/**
 * Reusable approval action bar for a campaign OR a single post (POST mode). Renders the actions
 * legal for the current state: Submit (draft/revise); Approve · Cancel Review · Request changes ·
 * Reject (+ Run compliance check while IN_REVIEW) under review. Request-changes / Reject invoke
 * their callbacks (the caller opens a comment modal — a comment is required server-side and lands
 * in the approval history). Cancel Review withdraws it back to Draft (author/admin — the server
 * enforces). `context='post'` renders nothing in terminal states (the post card shows the status);
 * `context='campaign'` shows a short status note.
 */
export function ApprovalActionBar({
  state,
  busy,
  canReview,
  canSubmit,
  context,
  pendingApproverNames,
  submitBlockedReason,
  readySubmitNote,
  onSubmit,
  onApprove,
  onCancelReview,
  onRequestChanges,
  onReject,
  onCompliance,
}: {
  state: ApprovalState;
  busy: boolean;
  /** Loose FE gate for reviewer decisions (server is authoritative). */
  canReview: boolean;
  /** Loose FE gate for submitter actions — submit + cancel-review. */
  canSubmit: boolean;
  context: 'campaign' | 'post';
  pendingApproverNames?: string[];
  /**
   * When set, submission is blocked (e.g. a post/campaign isn't fully scheduled yet): the Submit
   * button is disabled and this message replaces the "ready to submit" note. The server enforces
   * the same rule — this just surfaces WHY up front.
   */
  submitBlockedReason?: ReactNode;
  /**
   * What to say while submission is still open, in the caller's own words — a lane says "when the
   * blog posts are ready", which the generic campaign wording cannot.
   */
  readySubmitNote?: ReactNode;
  onSubmit: () => void;
  onApprove: () => void;
  onCancelReview: () => void;
  onRequestChanges: () => void;
  onReject: () => void;
  onCompliance: () => void;
}) {
  if (SUBMITTABLE_STATES.has(state)) {
    return (
      <ActionRow
        note={
          submitBlockedReason ||
          (state === ApprovalState.REVISE
            ? 'Changes were requested. Edit the content, then re-submit for review.'
            : (readySubmitNote ??
              (context === 'post'
                ? 'When this post is ready, submit it for review.'
                : 'When the campaign is ready, submit it for review.')))
        }
      >
        <Button
          disabled={!canSubmit || busy || Boolean(submitBlockedReason)}
          loading={busy}
          onClick={onSubmit}
        >
          <ShieldCheck className="h-4 w-4" aria-hidden />
          Submit for approval
        </Button>
      </ActionRow>
    );
  }

  if (REVIEW_STATES.has(state)) {
    const reviewNote =
      pendingApproverNames && pendingApproverNames.length > 0
        ? `Awaiting approval: ${pendingApproverNames.join(', ')}`
        : context === 'post'
          ? 'This post is awaiting a decision.'
          : 'This campaign is awaiting a decision.';
    // Healthcare packs detour a final Approve to COMPLIANCE_CHECK; from there the forward action
    // is "Clear compliance check" (COMPLIANCE_CHECK → APPROVED), NOT Approve (which 400s at that
    // state). Everywhere else the forward action is a normal Approve, which is what ENTERS the
    // compliance stage — so the button lives at COMPLIANCE_CHECK, never at IN_REVIEW (where it
    // would skip compliance).
    const atCompliance = state === ApprovalState.COMPLIANCE_CHECK;
    return (
      <ActionRow note={reviewNote}>
        {atCompliance ? (
          <Button variant="primary" disabled={!canReview || busy} onClick={onCompliance}>
            <ShieldCheck className="h-4 w-4" aria-hidden />
            Clear compliance check
          </Button>
        ) : (
          <Button variant="primary" disabled={!canReview || busy} onClick={onApprove}>
            <CheckCircle2 className="h-4 w-4" aria-hidden />
            Approve
          </Button>
        )}
        {/* Withdraw back to Draft — a submitter action (server: author or workspace admin). */}
        <Button variant="secondary" disabled={!canSubmit || busy} onClick={onCancelReview}>
          <RotateCcw className="h-4 w-4" aria-hidden />
          Cancel Review
        </Button>
        <Button variant="secondary" disabled={!canReview || busy} onClick={onRequestChanges}>
          <MessageSquareWarning className="h-4 w-4" aria-hidden />
          Request changes
        </Button>
        <Button variant="danger" disabled={!canReview || busy} onClick={onReject}>
          <XCircle className="h-4 w-4" aria-hidden />
          Reject
        </Button>
      </ActionRow>
    );
  }

  // Terminal / released states: a short note for the campaign; nothing for a post (its card badge
  // already shows the status).
  if (context === 'post') return null;
  if (state === ApprovalState.APPROVED) {
    return <ActionRow note="Approved — schedule or publish its posts below." />;
  }
  if (state === ApprovalState.SCHEDULED) {
    return <ActionRow note="Scheduled — its posts will publish at their set times." />;
  }
  if (state === ApprovalState.PUBLISHED) {
    return <ActionRow note="Published." />;
  }
  return <ActionRow note="This campaign was rejected." />;
}
